import type { Appearance } from "@stripe/stripe-js";
import { type RefObject, useEffect, useRef, useState } from "react";
import type { LockerverseStyle, LockerverseTheme } from "./theme.ts";

const DEFAULT_FONT_FAMILY =
  'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const CONTEXTUAL_CSS = /var\(|currentcolor|inherit|initial|unset|revert/i;

function resolveToken(
  root: HTMLElement,
  probe: HTMLElement,
  property: string,
  token: string,
  fallback: string
) {
  const view = root.ownerDocument.defaultView;
  if (!view) {
    return fallback;
  }
  const tokenValue = view
    .getComputedStyle(root)
    .getPropertyValue(`--lockerverse-${token}`)
    .trim();
  if (!tokenValue) {
    return fallback;
  }
  if (!view.CSS?.supports(property, tokenValue)) {
    return fallback;
  }
  if (!CONTEXTUAL_CSS.test(tokenValue)) {
    return tokenValue;
  }
  probe.style.removeProperty(property);
  probe.style.setProperty(property, tokenValue, "important");
  return (
    view.getComputedStyle(probe).getPropertyValue(property).trim() || fallback
  );
}

/** Resolve host CSS in the host document. Stripe's iframe cannot inherit it. */
function resolveAppearance(
  root: HTMLElement | null,
  theme: LockerverseTheme,
  accent?: string
): Appearance {
  const light = theme === "light";
  const variables: NonNullable<Appearance["variables"]> = {
    borderRadius: "10px",
    colorBackground: light ? "#f5f6f7" : "#121212",
    colorDanger: light ? "#c52b3e" : "#f08a98",
    colorPrimary: accent ?? "#38d77a",
    colorText: light ? "#121416" : "#ffffff",
    colorTextPlaceholder: light ? "#72767d" : "#6b6b6b",
    colorTextSecondary: light ? "#62666d" : "#a1a1aa",
    focusBoxShadow: "none",
    focusOutline: "none",
    fontFamily: DEFAULT_FONT_FAMILY,
    fontSizeBase: "16px",
  };
  const view = root?.ownerDocument.defaultView;
  if (root?.isConnected && view) {
    variables.fontFamily =
      view.getComputedStyle(root).fontFamily || DEFAULT_FONT_FAMILY;
    const probe = root.ownerDocument.createElement("span");
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText =
      "position:absolute;visibility:hidden;pointer-events:none;width:0;height:0;overflow:hidden";
    root.append(probe);
    try {
      const tokens = [
        ["borderRadius", "radius", "border-radius"],
        ["colorBackground", "control", "color"],
        ["colorDanger", "danger", "color"],
        ["colorPrimary", "accent", "color"],
        ["colorText", "text", "color"],
        ["colorTextPlaceholder", "placeholder", "color"],
        ["colorTextSecondary", "muted", "color"],
      ] as const;
      for (const [variable, token, property] of tokens) {
        variables[variable] = resolveToken(
          root,
          probe,
          property,
          token,
          variables[variable] ?? ""
        );
      }
    } finally {
      probe.remove();
    }
  }
  return {
    rules: {
      ".Input:focus": {
        borderColor: variables.colorPrimary ?? "#38d77a",
        boxShadow: `0 0 0 1px ${variables.colorPrimary}`,
        outline: "none",
      },
    },
    theme: light ? "stripe" : "night",
    variables,
  };
}

/** Keep the current Elements appearance while a payment or recovery is active. */
export function useStripeAppearance(
  root: RefObject<HTMLElement | null>,
  theme: LockerverseTheme | undefined,
  style: LockerverseStyle | undefined,
  locked: boolean,
  accent?: string
) {
  const [appearance, setAppearance] = useState<Appearance | null>(null);
  const resolved = useRef<Appearance | null>(null);
  useEffect(() => {
    if (locked && resolved.current) {
      return;
    }
    const next = resolveAppearance(root.current, theme ?? "dark", accent);
    if (JSON.stringify(resolved.current) !== JSON.stringify(next)) {
      resolved.current = next;
      setAppearance(next);
    }
  }, [root, theme, style, locked, accent]);
  return appearance;
}
